import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LngLat } from 'maplibre-gl';
import { firstValueFrom, forkJoin, Observable, of, pairwise, ReplaySubject, startWith, takeUntil } from 'rxjs';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import {
    MapboxStyleWithZoom,
    MapFieldInterventionSource,
    MapLayerSource,
} from '@connect-services/client/components/map/map.component';
import {
    changeSelectionFieldInterventionRegexp,
    createFieldInterventionRegexp,
    dashboardFieldInterventionRegexp,
    fieldInterventionEntitiesRegexp,
    fieldInterventionFormsRegexp,
    fieldInterventionLayersRegexp,
    planningFieldInterventionRegexp,
} from '@connect-services/client/guards/can-navigate.guard';
import { GeomLayer } from '@connect-services/client/models/geom-layer.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { AssertService } from '@connect-services/client/services/assert.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import {
    FieldIntervention,
    FieldInterventionsService,
    Layer,
    LayerControllerGet200Response,
    LayersService,
} from '@connect-services/src/sdk/api';
import { defaultPaginationParameters, paginationMaxTake } from '@shared/client/models/pagination.model';

@Injectable({
    providedIn: 'root',
})
export class HomeService {
    public readonly center: WritableSignal<LngLat | undefined> = signal(undefined);
    public readonly entityTableExtPageOpen = signal(false);
    public readonly isProjectInitialized = signal<boolean>(false);
    public readonly projectIds: WritableSignal<{ [key in 'previous' | 'current']: string }> = signal({
        current: '',
        previous: '',
    });
    public requestSubscriptions: { [projectId: string]: ReplaySubject<void> } = {};
    public readonly synopticExtPageOpen = signal(false);

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly fieldInterventionService = inject(FieldInterventionsService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly router = inject(Router);
    private readonly sdkLayersService = inject(LayersService);
    private readonly sourcesService = inject(SourcesService);

    public constructor() {
        if (!this.activatedRoute.params) {
            return;
        }

        this.activatedRoute.params
            .pipe(startWith(this.activatedRoute.snapshot.params), pairwise())
            .subscribe(([previous, current]) => {
                this.projectIds.set({ current: current?.['projectId'], previous: previous?.['projectId'] });

                if (previous?.['projectId'] === current?.['projectId'] && this.isProjectInitialized()) {
                    return;
                }

                if (!this.requestSubscriptions[current?.['projectId']]) {
                    this.requestSubscriptions[previous?.['projectId']]?.complete();

                    this.requestSubscriptions[current?.['projectId']] = new ReplaySubject(1);
                }
            });
    }

    public getStylesForSources<T extends { layerStyle: string | null }>(
        sources: T[],
    ): Observable<MapboxStyleWithZoom[]> {
        if (!sources.length) {
            return of([]);
        }
        const layerStyleIds: Set<string> = sources.reduce((acc, source) => {
            if (!source.layerStyle) {
                return acc;
            }

            acc.add(source.layerStyle);

            return acc;
        }, new Set<string>());

        if (!layerStyleIds.size) {
            return of([]);
        }

        return forkJoin(
            Array.from(layerStyleIds).map((layerStyleId) => this.sourcesService.getLayerStyle(layerStyleId)),
        ).pipe(takeUntil(this.requestSubscriptions[this.projectIds().current]));
    }

    public async initSources(setupProjectCenter: boolean = false): Promise<void> {
        if (this.isFieldInterventionUrl()) {
            return;
        }

        if (setupProjectCenter) {
            this.setupProjectCenter();
        }

        const [layers, fieldInterventions] = await Promise.all([
            this.getAllLayers(),
            firstValueFrom(
                this.fieldInterventionService.fieldInterventionControllerGet({ project: this.projectBloc.value()!.id }),
            ),
        ]);

        const [layerStyles, fieldInterventionStyles] = await Promise.all([
            firstValueFrom(this.getStylesForSources(layers.items)),
            firstValueFrom(this.getStylesForSources(fieldInterventions.items)),
        ]);

        const layerSources: MapLayerSource[] = layers.items.map(
            (layer: Layer): MapLayerSource =>
                new MapLayerSource(
                    GeomLayer.fromLayer(layer),
                    layerStyles.find((style: MapboxStyleWithZoom) => style.id === layer.layerStyle),
                ),
        );
        const fieldInterventionSources: MapFieldInterventionSource[] = fieldInterventions.items.map(
            (fieldIntervention: FieldIntervention): MapFieldInterventionSource =>
                new MapFieldInterventionSource(
                    fieldIntervention.id,
                    fieldIntervention,
                    fieldInterventionStyles.find(
                        (style: MapboxStyleWithZoom) => style.id === fieldIntervention.layerStyle,
                    )!,
                ),
        );

        this.setSources(layerSources, fieldInterventionSources);
    }

    public isExternalBoxPlanPage(): boolean {
        return this.checkCurrentRouteName(RoutePathEnum.SYNOPTIC_PLAN);
    }

    public isExternalPage(): boolean {
        return (
            this.router.url.includes('/ext/') ||
            this.router.url.includes('ext=true') ||
            this.checkCurrentRouteName(RoutePathEnum.EXT)
        );
    }

    public isFieldInterventionUrl(): boolean {
        return (
            createFieldInterventionRegexp.test(this.router.url) ||
            planningFieldInterventionRegexp.test(this.router.url) ||
            dashboardFieldInterventionRegexp.test(this.router.url) ||
            changeSelectionFieldInterventionRegexp.test(this.router.url) ||
            fieldInterventionEntitiesRegexp.test(this.router.url) ||
            fieldInterventionLayersRegexp.test(this.router.url) ||
            fieldInterventionFormsRegexp.test(this.router.url)
        );
    }

    public isSynopticPage(): boolean {
        return this.checkCurrentRouteName(RoutePathEnum.SYNOPTIC);
    }

    public resetSources(forceStyleReset: boolean = false): void {
        this.sourcesService.sources.set([]);

        if (!forceStyleReset) {
            return;
        }

        this.sourcesService.resetLayerStyles();
    }

    public setupProjectCenter(): void {
        const center = this.projectBloc.value()!.center?.coordinates as [number, number] | null;

        if (center) {
            this.center.set(new LngLat(center[0], center[1]));
        }

        if (!this.isProjectInitialized()) {
            this.isProjectInitialized.set(true);
        }
    }

    private checkCurrentRouteName(expectedRouteName: RoutePathEnum): boolean {
        const name = this.getCurrentRouteName();

        return !!name && expectedRouteName === name;
    }

    private async getAllLayers(layers?: LayerControllerGet200Response): Promise<LayerControllerGet200Response> {
        if (layers && !layers.hasNextPage) {
            return layers;
        }

        const page: number = layers ? layers.page + 1 : 0;
        const loadedLayers = await firstValueFrom(
            this.sdkLayersService.layerControllerGet({
                project: this.projectBloc.value()!.id,
                ...defaultPaginationParameters,
                page: page,
                take: paginationMaxTake,
            }),
        );

        loadedLayers.items.unshift(...(layers?.items ?? []));

        return this.getAllLayers(loadedLayers);
    }

    private getCurrentRouteName(): RoutePathEnum | null {
        const current = this.getDeepestRouteChild();
        const name = current?.snapshot?.data?.['name'];

        if (!name) {
            return null;
        }

        AssertService.isEnum<RoutePathEnum>(name, RoutePathEnum);

        return name;
    }

    private getDeepestRouteChild(): ActivatedRoute {
        let route = this.activatedRoute;
        while (route.firstChild) {
            route = route.firstChild;
        }

        return route;
    }

    private setSources(layerSources: MapLayerSource[], fieldInterventionSources: MapFieldInterventionSource[]): void {
        this.sourcesService.sources.set([
            ...layerSources,
            ...fieldInterventionSources.filter((source: MapFieldInterventionSource) => source.style.length),
        ]);
    }
}
